import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useMount, useSetState } from 'react-use';

import {
  CloseNodeAutomationAttributes,
  StopNodeAutomationAttributes,
} from 'components/powerFlowShared/components/nodeTypes/canvas/close';
import { useHasFeature } from 'components/shared/featureFlag';
import { FlowEditorChrome, FlowType, NodeDataT, NodeT } from 'fable/flowEditor';
import { AutomationNodeTypes } from 'hocs';
import type {
  AutomationFlowActionT,
  AutomationT,
  WithAutomationsInjectedPropsT,
} from 'hocs';
import { useNotifications } from 'hooks/useNotifications';

import { getActionNodes } from '../../powerFlowShared/helpers/getInvalidNodes';

import { getInvalidReplies } from './automationFlow-utils';
import {
  CloseNode,
  ConditionNode,
  InitNode,
  QueueNode,
  ReplyNode,
  StopNode,
  TagNode,
  WaitNode,
} from './nodeTypes';
import { ConditionNodeAttributes } from './nodeTypes/canvas/condition';
import { InitNodeAttributes } from './nodeTypes/canvas/init';
import { QueueNodeAutomationAttributes } from './nodeTypes/canvas/queue';
import { ReplyNodeAttributes } from './nodeTypes/canvas/reply';
import { TagsNodeAttributes } from './nodeTypes/canvas/tags';
import { WaitNodeAttributes } from './nodeTypes/canvas/wait';
import type { AutomationFlowExtraDataT } from './types';

type PropsT = WithAutomationsInjectedPropsT;

type StateT = {
  ready: boolean;
  saveStatus: number;
  automation: null | AutomationT;
  invalidNodes: NodeT['id'][] | null;
  errorMessage: null | string;
  nodeTypes: {};
};

enum SaveStatus {
  Failed = -1,
  Unsaved = 0,
  Saving = 1,
  Saved = 2,
}

type ValidationT = {
  invalidNodes: NodeT['id'][];
  errorMessage: null | string;
};

export const AutomationFlow = (props: PropsT): JSX.Element => {
  const isCanvasEnabled = useHasFeature('routing-use-canvas');
  const params = useParams();
  const notify = useNotifications();

  const [state, setState] = useSetState<StateT>({
    ready: false,
    saveStatus: SaveStatus.Saved,
    automation: null,
    invalidNodes: null,
    errorMessage: null,
    nodeTypes: isCanvasEnabled
      ? {
          [AutomationNodeTypes.INIT]: InitNodeAttributes,
          // GREEN - Termination -> Queue
          [AutomationNodeTypes.QUEUE]: QueueNodeAutomationAttributes,
          // RED - Termination -> Closed
          [AutomationNodeTypes.STOP]: StopNodeAutomationAttributes,
          [AutomationNodeTypes.CLOSE]: CloseNodeAutomationAttributes,
          // PURPLE - Splitters
          [AutomationNodeTypes.CONDITION]: ConditionNodeAttributes,
          [AutomationNodeTypes.WAIT]: WaitNodeAttributes,
          // BLUE - Other
          [AutomationNodeTypes.TAG]: TagsNodeAttributes,
          [AutomationNodeTypes.REPLY]: ReplyNodeAttributes,
        }
      : {
          [AutomationNodeTypes.INIT]: InitNode,
          // GREEN - Termination -> Queue
          [AutomationNodeTypes.QUEUE]: QueueNode,
          // RED - Termination -> Closed
          [AutomationNodeTypes.STOP]: StopNode,
          [AutomationNodeTypes.CLOSE]: CloseNode,
          // PURPLE - Splitters
          [AutomationNodeTypes.CONDITION]: ConditionNode,
          [AutomationNodeTypes.WAIT]: WaitNode,
          // BLUE - Other
          [AutomationNodeTypes.TAG]: TagNode,
          [AutomationNodeTypes.REPLY]: ReplyNode,
        },
  });

  useMount(() => {
    const { automations } = props;

    if (params.automationId) {
      automations.getItem(params.automationId).then((res) => {
        setState({
          ready: true,
          automation: res.data,
        });
      });
    }
  });

  const handleOnChange = (automation: NodeDataT<AutomationFlowActionT[]>) => {
    setState({
      saveStatus: SaveStatus.Unsaved,
      automation: automation as AutomationT,
      invalidNodes: null,
    });
  };

  const validateNodes = (): ValidationT => {
    const { automation } = state;
    const actions = automation?.actions || [];
    const nodesConnectedToActionNode = getActionNodes(actions);
    if (nodesConnectedToActionNode.length) {
      return {
        invalidNodes: nodesConnectedToActionNode,
        errorMessage: 'Action need to be selected',
      };
    }

    const invalidReplies = getInvalidReplies(actions);
    if (invalidReplies.length) {
      return {
        invalidNodes: invalidReplies,
        errorMessage: 'Min. 2 hours wait between auto-replies',
      };
    }

    return { invalidNodes: [], errorMessage: null };
  };

  const handleOnSave = () => {
    const { automations } = props;
    const { automation } = state;
    const validationResult = validateNodes();

    if (validationResult.invalidNodes.length > 0) {
      setState(validationResult);
    } else if (automation?.id) {
      setState({
        saveStatus: SaveStatus.Saving,
        invalidNodes: null,
        errorMessage: null,
      });

      automations
        .update(automation.id, automation)
        .then((res) => {
          setState({
            saveStatus: SaveStatus.Saved,
          });
        })
        .catch(() => {
          setState({
            saveStatus: SaveStatus.Failed,
          });
          notify({
            title: 'An error occurred',
            text: `An error occurred when trying to save automation`,
            icon: 'exclamation',
            className: 'danger',
          });
        });
    }
  };

  const handleOnActivate = (automation: NodeDataT<AutomationFlowActionT[]>) => {
    setState({
      automation: automation as AutomationT,
    });

    if (automation?.id) {
      props.automations.update(automation.id, {
        active: true,
      });
    }
  };

  const hasUnsavedChanges = () => {
    return state.saveStatus <= SaveStatus.Unsaved;
  };

  const getExtraData = useCallback(
    (automation: AutomationT): AutomationFlowExtraDataT => {
      return {
        channels: automation.channels,
        trigger: automation.trigger,
      };
    },
    [],
  );

  const { nodeTypes, automation, invalidNodes, errorMessage } = state;

  return (
    <div className="powercallflow-container">
      {automation !== null && (
        <FlowEditorChrome
          flowType={FlowType.FLOW_TYPE_AUTOMATION}
          goBackPath="/settings/automations"
          onChange={handleOnChange}
          onSave={handleOnSave}
          onActivate={handleOnActivate}
          nodeTypes={nodeTypes}
          initialData={automation}
          hasUnsavedChanges={hasUnsavedChanges()}
          extraData={getExtraData(automation)}
          invalidNodes={invalidNodes}
          errorMessage={errorMessage}
          navigatePromptProps={{
            message:
              'When you leave this page, all unsaved changes to your automation will be lost.',
          }}
        />
      )}
    </div>
  );
};
